/**
 * @fileoverview Hook e utilitários para engine de máscara de input
 * Fornece funções para aplicar máscaras, calcular posição do cursor,
 * e detectar o inputMode apropriado baseado no padrão da máscara.
 *
 * @module useMaskEngine
 */

/**
 * Caracteres de formato padrão suportados pela máscara.
 * Cada chave representa um caractere especial na máscara,
 * e o valor é a regex que define quais caracteres são aceitos.
 *
 * @constant {Object.<string, RegExp>}
 * @property {RegExp} 9 - Aceita apenas dígitos numéricos (0-9)
 * @property {RegExp} A - Aceita apenas letras (a-z, A-Z)
 * @property {RegExp} * - Aceita alfanuméricos (letras e números)
 * @property {RegExp} # - Aceita caracteres hexadecimais (0-9, a-f, A-F)
 * @property {RegExp} S - Aceita letras e espaços
 *
 * @example
 * // Máscara de telefone BR: "(99) 9 9999-9999"
 * // Máscara de CPF: "999.999.999-99"
 * // Máscara de placa: "AAA-9*99"
 * // Máscara de cor hex: "#######"
 */
export const DEFAULT_FORMAT_CHARS = {
  '9': /\d/,
  A: /[a-zA-Z]/,
  '*': /[a-zA-Z0-9]/,
  '#': /[0-9a-fA-F]/,
  S: /[a-zA-Z\s]/,
};

/**
 * Verifica se um caractere na máscara é um literal (não é um placeholder de formato).
 *
 * @param {string} char - Caractere a ser verificado
 * @param {Object.<string, RegExp>} formatChars - Mapa de caracteres de formato
 * @returns {boolean} True se o caractere é um literal (parênteses, traços, pontos, etc.)
 *
 * @example
 * isCharLiteral('(', DEFAULT_FORMAT_CHARS); // true
 * isCharLiteral('9', DEFAULT_FORMAT_CHARS); // false
 */
export const isCharLiteral = (char, formatChars) => {
  return !Object.prototype.hasOwnProperty.call(formatChars, char);
};

/**
 * Obtém todos os índices de caracteres de formato (não-literais) na máscara.
 *
 * @param {string} mask - String da máscara
 * @param {Object.<string, RegExp>} formatChars - Mapa de caracteres de formato
 * @returns {number[]} Array de índices onde há caracteres de formato
 *
 * @example
 * getFormatCharIndices('(99) 9999', DEFAULT_FORMAT_CHARS);
 * // Retorna: [1, 2, 5, 6, 7, 8]
 */
export const getFormatCharIndices = (mask, formatChars) => {
  const indices = [];
  for (let i = 0; i < mask.length; i++) {
    if (!isCharLiteral(mask[i], formatChars)) {
      indices.push(i);
    }
  }
  return indices;
};

/**
 * Aplica uma máscara a um valor de entrada, retornando o valor mascarado e o valor raw.
 * A máscara é aplicada progressivamente - literais só aparecem quando há caracteres após eles.
 *
 * @param {string} inputValue - Valor digitado pelo usuário (pode conter caracteres da máscara ou não)
 * @param {string} mask - String da máscara (ex: "(99) 9 9999-9999")
 * @param {Object.<string, RegExp>} [formatChars=DEFAULT_FORMAT_CHARS] - Mapa de caracteres de formato
 * @returns {{ masked: string, raw: string }} Objeto com valor mascarado e valor raw (apenas caracteres válidos)
 *
 * @example
 * applyMask('11999998888', '(99) 9 9999-9999');
 * // Retorna: { masked: '(11) 9 9999-8888', raw: '11999998888' }
 *
 * @example
 * applyMask('ABC123', 'AAA-999');
 * // Retorna: { masked: 'ABC-123', raw: 'ABC123' }
 */
export const applyMask = (
  inputValue,
  mask,
  formatChars = DEFAULT_FORMAT_CHARS,
) => {
  if (!mask) {
    return { masked: inputValue || '', raw: inputValue || '' };
  }

  if (!inputValue || inputValue.length === 0) {
    return { masked: '', raw: '' };
  }

  // Extrai apenas caracteres que passam na validação dos formatChars
  // Ignora literais da máscara presentes no input
  const literalsInMask = new Set();
  for (let i = 0; i < mask.length; i++) {
    if (isCharLiteral(mask[i], formatChars)) {
      literalsInMask.add(mask[i]);
    }
  }

  const validChars = [];
  for (let i = 0; i < inputValue.length; i++) {
    const char = inputValue[i];

    if (!literalsInMask.has(char)) {
      let isValidForSomeFormat = false;
      for (const regex of Object.values(formatChars)) {
        if (regex.test(char)) {
          isValidForSomeFormat = true;
          break;
        }
      }
      if (isValidForSomeFormat) {
        validChars.push(char);
      }
    }
  }

  if (validChars.length === 0) {
    return { masked: '', raw: '' };
  }

  let masked = '';
  let raw = '';
  let validCharIndex = 0;
  let maskIndex = 0;

  while (maskIndex < mask.length && validCharIndex < validChars.length) {
    const maskChar = mask[maskIndex];

    if (isCharLiteral(maskChar, formatChars)) {
      masked += maskChar;
      maskIndex++;
    } else {
      const regex = formatChars[maskChar];
      const inputChar = validChars[validCharIndex];

      if (regex && regex.test(inputChar)) {
        masked += inputChar;
        raw += inputChar;
        validCharIndex++;
        maskIndex++;
      } else {
        validCharIndex++;
      }
    }
  }

  let trimIndex = masked.length;
  for (let i = masked.length - 1; i >= 0; i--) {
    if (literalsInMask.has(masked[i])) {
      trimIndex = i;
    } else {
      break;
    }
  }
  masked = masked.substring(0, trimIndex);

  return { masked, raw };
};

/**
 * Calcula a nova posição do cursor após aplicar a máscara.
 * Considera caracteres literais e ajusta a posição para ficar
 * no próximo caractere editável.
 *
 * @param {number} prevCursorPos - Posição anterior do cursor (antes da edição)
 * @param {string} prevMasked - Valor mascarado anterior
 * @param {string} newMasked - Novo valor mascarado
 * @param {string} mask - String da máscara
 * @param {Object.<string, RegExp>} formatChars - Mapa de caracteres de formato
 * @param {boolean} isDeleting - Se o usuário está deletando (backspace/delete)
 * @returns {number} Nova posição do cursor
 *
 * @example
 * // Após digitar '1' em "(|99) 9999", cursor deve ir para posição 2
 * calculateCursorPosition(1, '', '(1', '(99) 9999', DEFAULT_FORMAT_CHARS, false);
 * // Retorna: 2
 */
export const calculateCursorPosition = (
  prevCursorPos,
  prevMasked,
  newMasked,
  mask,
  formatChars,
  isDeleting,
) => {
  if (!newMasked || newMasked.length === 0) {
    return 0;
  }

  if (!mask) {
    return newMasked.length;
  }

  const prevLength = prevMasked?.length || 0;
  const newLength = newMasked.length;
  const lengthDiff = newLength - prevLength;

  if (isDeleting) {
    // Ao deletar, calculamos onde o cursor deve ficar
    // O cursor estava em prevCursorPos, e o valor encolheu

    // Se apagamos tudo, vai para o início
    if (newLength === 0) {
      return 0;
    }

    // Calcula a nova posição baseada na diferença de tamanho
    // O cursor deve ficar onde estava, ajustado pela mudança
    let newPos = prevCursorPos + lengthDiff;

    // Garante que não ultrapassa os limites
    newPos = Math.max(0, Math.min(newPos, newLength));

    // Se a posição cair em um literal, volta para o caractere válido anterior
    while (
      newPos > 0 &&
      newPos <= newLength &&
      isCharLiteral(mask[newPos - 1], formatChars) &&
      newMasked[newPos - 1] === mask[newPos - 1]
    ) {
      newPos--;
    }

    return newPos;
  }

  // Ao adicionar caracteres
  // O cursor avança pela quantidade de caracteres adicionados (incluindo literais)
  let newPos = prevCursorPos + Math.abs(lengthDiff);

  // Pula literais que foram adicionados automaticamente
  while (
    newPos < newLength &&
    newPos < mask.length &&
    isCharLiteral(mask[newPos], formatChars)
  ) {
    newPos++;
  }

  return Math.min(newPos, newLength);
};

/**
 * Detecta o inputMode mais apropriado baseado nos caracteres de formato usados na máscara.
 * Isso ajuda dispositivos móveis a exibir o teclado correto.
 *
 * @param {string} mask - String da máscara
 * @param {Object.<string, RegExp>} [formatChars=DEFAULT_FORMAT_CHARS] - Mapa de caracteres de formato
 * @returns {'numeric' | 'text' | 'tel'} O inputMode recomendado
 *
 * @example
 * detectInputMode('(99) 9 9999-9999'); // 'tel' (parece telefone)
 * detectInputMode('999.999.999-99'); // 'numeric' (só números)
 * detectInputMode('AAA-9999'); // 'text' (tem letras)
 */
export const detectInputMode = (mask, formatChars = DEFAULT_FORMAT_CHARS) => {
  if (!mask) return 'text';

  const formatCharsInMask = new Set();

  for (let i = 0; i < mask.length; i++) {
    const char = mask[i];
    if (!isCharLiteral(char, formatChars)) {
      formatCharsInMask.add(char);
    }
  }

  // Se contém apenas '9', é numérico
  const hasOnlyDigits =
    formatCharsInMask.size === 1 && formatCharsInMask.has('9');
  const hasLetters =
    formatCharsInMask.has('A') ||
    formatCharsInMask.has('S') ||
    formatCharsInMask.has('*');

  if (hasOnlyDigits) {
    // Verifica se a máscara parece um telefone (10-15 dígitos)
    const maskDigitsOnly = mask.replace(/[^9]/g, '');
    if (maskDigitsOnly.length >= 10 && maskDigitsOnly.length <= 15) {
      return 'tel';
    }

    return 'numeric';
  }

  if (hasLetters) {
    return 'text';
  }

  // Hex ou outros
  if (formatCharsInMask.has('#')) {
    return 'text';
  }

  return 'text';
};

/**
 * Obtém o placeholder visual da máscara, substituindo caracteres de formato
 * pelo caractere de placeholder especificado.
 *
 * @param {string} mask - String da máscara
 * @param {string} [placeholderChar='_'] - Caractere a usar como placeholder
 * @param {Object.<string, RegExp>} [formatChars=DEFAULT_FORMAT_CHARS] - Mapa de caracteres de formato
 * @returns {string} Placeholder visual da máscara
 *
 * @example
 * getMaskPlaceholder('(99) 9 9999-9999', '_');
 * // Retorna: '(__) _ ____-____'
 */
export const getMaskPlaceholder = (
  mask,
  placeholderChar = '_',
  formatChars = DEFAULT_FORMAT_CHARS,
) => {
  if (!mask) return '';

  let placeholder = '';
  for (let i = 0; i < mask.length; i++) {
    const char = mask[i];
    if (isCharLiteral(char, formatChars)) {
      placeholder += char;
    } else {
      placeholder += placeholderChar;
    }
  }
  return placeholder;
};

/**
 * Valida se um valor raw está completo de acordo com a máscara.
 *
 * @param {string} rawValue - Valor raw (sem máscara)
 * @param {string} mask - String da máscara
 * @param {Object.<string, RegExp>} [formatChars=DEFAULT_FORMAT_CHARS] - Mapa de caracteres de formato
 * @returns {boolean} True se o valor está completo
 *
 * @example
 * isComplete('11999998888', '(99) 9 9999-9999'); // true
 * isComplete('1199999', '(99) 9 9999-9999'); // false
 */
export const isComplete = (
  rawValue,
  mask,
  formatChars = DEFAULT_FORMAT_CHARS,
) => {
  if (!mask || !rawValue) return false;

  const formatCharCount = getFormatCharIndices(mask, formatChars).length;
  return rawValue.length === formatCharCount;
};

/**
 * Hook customizado para gerenciar a lógica da máscara (exporta as funções para uso externo).
 * Pode ser usado diretamente ou através do componente InputMaskV2.
 *
 * @returns {Object} Objeto com todas as funções utilitárias da engine
 */
const useMaskEngine = () => {
  return {
    applyMask,
    calculateCursorPosition,
    detectInputMode,
    getMaskPlaceholder,
    isComplete,
    isCharLiteral,
    getFormatCharIndices,
    DEFAULT_FORMAT_CHARS,
  };
};

export default useMaskEngine;
